import { Box, Divider, Group, Stack, createStyles } from '@mantine/core';
import type { DocumentCreationProposal } from '@repo/api-codegen';
import { IconEmoji } from '@repo/common/components/IconEmoji/IconEmoji';
import { Text } from '@repo/foundations';
import { Badge } from '@repo/foundations/components/Badge';
import { RichEditor } from '../../../RichEditor';
import { OwnerRender, TeamRender } from '../../../TableV2/render';

const useStyles = createStyles((theme) => ({
	emojiContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: theme.radius.sm,
		background: theme.other.getColor('surface/secondary/default'),
		width: theme.spacing.xl,
		height: theme.spacing.xl,
	},
	fieldTitle: {
		minWidth: 80,
	},
}));

// Manually defining interface if not available in api-codegen yet,
// matching backend CreateDocumentToolArgs
export interface DocumentCreationProposalData {
	title: string;
	definition: string;
	icon?: string | null;
	published?: boolean;
	teams?: string[] | null;
	owners?: string[] | null;
	collections?: string[] | null;
	proposal_type: 'document_creation';
}

export function DocumentSuggestionRenderer({
	data,
}: {
	data: DocumentCreationProposal;
}) {
	const { classes } = useStyles();
	const proposalData = data.data;
	const hasOwner = !!proposalData.owners && proposalData.owners.length > 0;
	const hasTeam = !!proposalData.teams && proposalData.teams.length > 0;

	return (
		<Stack spacing="md" p="xl">
			{/* Header */}
			<Group spacing="sm">
				<Box className={classes.emojiContainer}>
					<IconEmoji value={proposalData.icon || 'iconName:file'} size={28} />
				</Box>
				<Text size="xxl" weight="bold">
					{proposalData.title}
				</Text>
				{proposalData.published && <Badge variant="success">Published</Badge>}
			</Group>

			{/* Meta Section */}
			<Box>
				<Stack spacing={0}>
					{hasOwner && (
						<Group spacing={0} align="center" noWrap>
							<Box className={classes.fieldTitle}>
								<Text size="sm" color="text/secondary/default">
									Owner
								</Text>
							</Box>
							<Box ml="lg">
								<OwnerRender
									record={{
										id: 'owner',
										integration: '',
										owners: proposalData.owners || [],
										owners_groups: [],
									}}
								/>
							</Box>
						</Group>
					)}
					{hasTeam && (
						<Group spacing={0} align="center" noWrap>
							<Box className={classes.fieldTitle}>
								<Text size="sm" color="text/secondary/default">
									Teams
								</Text>
							</Box>
							<Box ml="md">
								<TeamRender
									record={{
										id: 'team',
										teams: proposalData.teams || [],
									}}
								/>
							</Box>
						</Group>
					)}
				</Stack>
			</Box>
			<Divider my="sm" />
			<RichEditor value={proposalData.definition || ''} readOnly />
		</Stack>
	);
}
